// @flow

import * as React from "react";
import { Link } from "react-router-dom";
import cx from "classnames";

import styles from "./Button.scss";
import Preloader from "../Preloader";

type ButtonProps = {
  children: React.Node,
  theme: "dark" | "light" | "clean",
  hasShadow: boolean,
  isDisabled: boolean,
  hasButtonAttributeDisabled: boolean,
  withIcon: Boolean,
  icon: () => string,
  size: "small" | "medium" | "large",
  asLink: boolean,
  asRouterLink: boolean,
  type: "button" | "submit",
  isLoading: boolean,
  withMargin?: boolean,
  customStyles: string,
  href?: string,
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
};

const Button = ({
  icon,
  withIcon,
  children,
  theme,
  hasShadow,
  isDisabled,
  hasButtonAttributeDisabled,
  size,
  asLink,
  asRouterLink,
  type,
  isLoading,
  withMargin,
  customStyles,
  ...props
}: ButtonProps) => {
  const classes = cx(styles.container, styles[theme], customStyles, {
    [styles.withShadow]: hasShadow,
    [styles.withMargin]: withMargin,
    [styles.disabled]: isDisabled,
    [styles.large]: size === "large",
    [styles.medium]: size === "medium",
    [styles.small]: size === "small"
  });

  const onClickHandler = e => {
    if (isDisabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (props.onClick) {
      props.onClick(e);
    }
  };

  const content = isLoading ? (
    <Preloader size="small" inverted />
  ) : (
    <>
      {withIcon && <span className={styles.icon}>{icon()}</span>}
      {children}
    </>
  );

  const { href } = props;
  if (asRouterLink && href) {
    return (
      <Link className={classes} to={href} {...props}>
        {content}
      </Link>
    );
  }

  return asLink ? (
    <a className={classes} {...props}>
      {content}
    </a>
  ) : (
    <button
      // eslint-disable-next-line react/button-has-type
      type={type}
      className={classes}
      disabled={isDisabled && hasButtonAttributeDisabled}
      aria-disabled={isDisabled}
      {...props}
      onClick={onClickHandler}
    >
      {content}
    </button>
  );
};

Button.defaultProps = {
  theme: "dark",
  hasShadow: false,
  isDisabled: false,
  hasButtonAttributeDisabled: true,
  withIcon: false,
  icon: () => null,
  size: "large",
  asLink: false,
  isLoading: false,
  type: "button",
  customStyles: "",
  withMargin: false
};

export default Button;
